import { faEllipsis, faFolderArrowDown, faPenToSquare, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { hasClearanceLevelForAction } from "userful-chronos-app-common-js/dist/authenticataion/roles";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { NOT_SET_VALUE, UserfulSecurityData } from "userful-chronos-app-common-js/dist/models/common";
import { CompGroup } from "userful-chronos-app-common-js/dist/models/group/Groups";
import { SamSourceDef } from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import { UserPermission } from "userful-chronos-app-common-js/dist/models/user-permission";
import {
    sourceSliceActions,
    useSourceAssetDispatch,
    useSourceAssetSelector,
} from "userful-chronos-common-store/dist/sourceassets";
import { useUsermgtSelector } from "userful-chronos-common-store/dist/usermgt";
import Button from "../button/Button";
import Dropdown from "../dropdown/Dropdown";
import { IDropdownItem } from "../dropdown/dropdownUtils";
import { convertSourceDefToAssetsData, SourceDeletionDialog } from "../sourceassets";

interface IProps {
    value: SamSourceDef;
    parent?: CompGroup;
    trigger?: React.ReactNode;
    onMoveOut: () => void;
    onEdit: (value: SamSourceDef, parent?: CompGroup) => void;
}

export default function SourceActionsMenu({ value, onMoveOut, trigger, parent, onEdit }: IProps) {
    const { t } = useTranslation();
    const dispatch = useSourceAssetDispatch();

    const globalStates = getGlobalStates();
    const userPermission = globalStates.userPermission as UserPermission | null;
    const userID = globalStates.userData?.userID;
    const actions = globalStates.actions;
    const sourceSpecs = useSourceAssetSelector((state) => state.sourceStore.sourceSpecs) || [];

    const allUsers = useUsermgtSelector((state) => state.usermgtStore.usernameIDs) || [];

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [ownerName, setOwnerName] = useState<string | undefined>(value.userfulSecurityData?.userName);

    useEffect(() => {
        setOwnerName(value.userfulSecurityData?.userName);
    }, [value.userfulSecurityData?.userName, value.id.value]);

    const canEdit = (securityData: UserfulSecurityData) => {
        if (!securityData || !userID || !userPermission) {
            return false;
        }
        return hasClearanceLevelForAction(securityData, userID, userPermission, actions, "edit");
    };

    const sanitizeparamAsset = (item: SamSourceDef) => {
        const paramAsset = convertSourceDefToAssetsData(item);
        const selectedSourceSpec = sourceSpecs.find((spec) => spec.header.id.value === item.type.value);
        if (!selectedSourceSpec) {
            return [];
        }
        return paramAsset.filter(
            (param) =>
                selectedSourceSpec.assetSpecs.findIndex((asset) => asset.sourceParamName === param.paramName) >= 0,
        );
    };

    const menuAllowed =
        (canEdit(value.userfulSecurityData) && userPermission?.isSourceSuppressor) ||
        userPermission?.canOverrideSecurityDataOwnerShip;
    if (!menuAllowed) {
        return null;
    }

    const onSave = (sourceValue: SamSourceDef, data: UserfulSecurityData) => {
        const update = {
            ...sourceValue,
            userfulSecurityData: data,
            assets: sanitizeparamAsset(sourceValue),
            appInstanceID: {
                value: getGlobalStates().systemID,
                appID: {
                    value: getGlobalStates().appID.value,
                },
            },
        };
        delete (update as any).auditData;
        dispatch(sourceSliceActions.forceUpdateSourceToServer(update));
    };

    const ownershipAllowed = Boolean(
        userPermission?.canOverrideSecurityDataOwnerShip &&
        value.userfulSecurityData?.clearanceLevelPermissions?.length,
    );

    const ownerItems: IDropdownItem | null = useMemo(() => {
        if (!ownershipAllowed) return null;

        const currentOwnerName = ownerName ?? NOT_SET_VALUE;

        const children: IDropdownItem[] = [
            {
                id: "owner-none",
                title: "Shared.none",
                options: "radio" as const,
                value:
                    currentOwnerName === NOT_SET_VALUE || !currentOwnerName
                        ? ("selected" as const)
                        : ("unselected" as const),
            },
            ...allUsers.map((user) => ({
                id: `owner-${user.userID.value}`,
                title: user.userName,
                options: "radio" as const,
                value: currentOwnerName === user.userName ? ("selected" as const) : ("unselected" as const),
            })),
        ];

        return {
            id: "owner-set",
            title: "Shared.SetOwner",
            children,
            doNotCloseOnClick: false,
        };
    }, [allUsers, ownershipAllowed, ownerName]);

    const dropdownItems: IDropdownItem[] = useMemo(() => {
        const items: IDropdownItem[] = [];

        if (canEdit(value.userfulSecurityData) && userPermission?.isSourceSuppressor) {
            items.push({
                id: "edit",
                icon: <FontAwesomeIcon icon={faPenToSquare} />,
                title: "Shared.edit",
            });
        }

        items.push({
            id: "move-out",
            icon: <FontAwesomeIcon icon={faFolderArrowDown} />,
            title: "Shared.move",
        });

        if (canEdit(value.userfulSecurityData) && userPermission?.isSourceSuppressor) {
            items.push({
                id: "delete",
                icon: <FontAwesomeIcon icon={faTrashAlt} />,
                title: "Shared.delete",
            });
        }

        if (ownerItems) {
            items.push(ownerItems);
        }

        return items;
    }, [actions, ownerItems, userPermission, userID, value.userfulSecurityData]);

    const handleDropdownChange = (item: IDropdownItem) => {
        if (item.id === "edit") {
            // Defer edit handling to consumer
            onEdit?.(value, parent);
            return;
        }

        if (item.id === "move-out") {
            onMoveOut();
            return;
        }

        if (item.id === "delete") {
            setDeleteOpen(true);
            return;
        }

        if (item.id.startsWith("owner-")) {
            const rawId = item.id.replace("owner-", "");
            const nextUserId = rawId === "none" ? NOT_SET_VALUE : rawId;
            const nextUser = rawId === "none" ? null : allUsers.find((u) => u.userID.value === rawId);
            const nextUserName = nextUser ? nextUser.userName : NOT_SET_VALUE;

            setOwnerName(nextUserName);

            const updatedSecurityData: UserfulSecurityData = {
                ...value.userfulSecurityData,
                userID: nextUserId,
                userName: nextUserName,
            };
            onSave(value, updatedSecurityData);
            return;
        }
    };

    return (
        <div onClick={(event) => event.stopPropagation()}>
            <Dropdown
                items={dropdownItems}
                onChange={handleDropdownChange}
                trigger={
                    trigger ? (
                        trigger
                    ) : (
                        <Button
                            className="source-actions-menu-toggle"
                            variant="ghost"
                            size="small"
                            iconOnly
                            aria-label={t("Shared.actions")}
                        >
                            <FontAwesomeIcon icon={faEllipsis} />
                        </Button>
                    )
                }
                usePortal
            />

            {canEdit(value.userfulSecurityData) && userPermission?.isSourceSuppressor && (
                <SourceDeletionDialog source={value} open={deleteOpen} onOpenChange={setDeleteOpen} hideTrigger />
            )}
        </div>
    );
}
